import {
  useState, useMemo, useCallback, FormEvent, useRef,
} from 'react';
import { CommonFilterForm, GlobalFilterObjectField } from '@type/filters';
import { SEARCH_FORM_FILTERS_OBJECT } from '@components/features/filters/helpers/searchFiltersObject';
import { mapSearchFilterToGlobalFilter } from '@components/features/filters/helpers/mapSearchFilterToGlobalFilter';
import { ComboBox, Input, Label } from '@components/common/Forms';
import { useTranslation } from 'next-i18next';
import { Chip } from '@components/common';
import { filterStrings } from '@utils/arrays/filter-strings';

const spokenLanguagesFilterObject = mapSearchFilterToGlobalFilter(SEARCH_FORM_FILTERS_OBJECT.spokenLanguages, 'checkbox');

/**
 * @description Generates the fields for the Spoken Languages filter group.
 * * **recordFunc** - the 'record' function from useBjForm.
 * * **submitFunc** - the function to trigger the form submit. You might want to trigger it on blur. Defaults to undefined.
 * * **idPrefix** - the prefix for the form elements ids. When using multiple forms on the same page,
 * this is needed to avoid duplicate ids.
 */
export const JobSearchSpokenLanguagesForm = (props: CommonFilterForm) => {
  // Destructure props
  const {
    recordFunc,
    idPrefix,
    submitFunc,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Initial input value
  const initialInputValue = useRef('');

  // COMBOBOX HELPERS
  // *******************************************************************************************************************
  // Combobox filter languages from the list
  // **********************************************
  const filterObjectList = (inputValue: string | undefined) => filterStrings(inputValue, 'includes', ['label']);


  // Combobox options
  // **********************************************
  const comboboxOptions = {
    staticList: spokenLanguagesFilterObject.fields,
    forceRefreshStaticList: true,
    clearOnSelect: true,
    valueFromKey: 'label',
    // Pass a filtering function to the combobox so when the user types in the input, the list is filtered
    filteringFunction: filterObjectList,
    freeSolo: false,
    showResetButton: false,
  };

  const [
    selectedLanguages, setSelectedLanguages,
  ] = useState<GlobalFilterObjectField[]>([]);

  // Add a selected language from the combobox to the selectedLanguages array
  // Add it only if it's not already in the selectedLanguages array
  // **********************************************
  const addLanguage = (selectedLanguage: GlobalFilterObjectField) => {
    setSelectedLanguages((current) => {
      const newLanguages = [...current];
      const newLanguage = selectedLanguage;
      if (!newLanguages.find((language) => language.recordOptions?.defaultValue === newLanguage.recordOptions?.defaultValue)) {
        newLanguages.push(newLanguage);
      }
      return newLanguages;
    });
  };

  // Delete a language from the selectedLanguages array
  // **********************************************
  const deleteLanguage = (languageValue: string) => {
    setSelectedLanguages((current) => {
      const newLanguages = [...current];
      return newLanguages.filter((language) => language.recordOptions?.defaultValue !== languageValue);
    });
  };

  // Convert the selectedLanguages array to a string to be used in the hidden input (or wherever needed)
  // **********************************************
  const selectedLanguagesString = useMemo(() => {
    let languagesString = '';

    selectedLanguages.forEach((language, index) => {
      languagesString += `${language.recordOptions?.defaultValue as string}${index < selectedLanguages.length - 1 ? ',' : ''}`;
    });

    return languagesString;
  }, [selectedLanguages]);

  // Convert the selectedLanguages array to a string to be used for filters
  // **********************************************
  const selectedLanguagesFiltersLabel = useMemo(() => {
    let languagesString = '';

    selectedLanguages.forEach((language, index) => {
      languagesString += `${language.recordOptions?.filtersLabel as string}${index < selectedLanguages.length - 1 ? '; ' : ''}`;
    });

    return languagesString;
  }, [selectedLanguages]);

  // We need to be able to update the selected languages when updated programmatically from the outside
  // of component (ie: by setFormValues() function) or URL query params
  // When using this function, don't update the selected languages if the event is trusted (user input) or currently updating the languages
  // Programatic updates are always not trusted (event.isTrusted = false)
  // **********************************************
  const buildSelectedLanguagesFromString = useCallback((event: FormEvent<HTMLInputElement>) => {
    // Helper variables
    const inputValue = event.currentTarget.value;
    initialInputValue.current = inputValue;
    const newLanguages: GlobalFilterObjectField[] = [];

    if (inputValue) {
      const languagesArray = inputValue.split(',');
      languagesArray.forEach((lang) => {
        const foundLanguage = spokenLanguagesFilterObject.fields.find((filterLanguage) => filterLanguage.recordOptions?.defaultValue === lang);
        if (foundLanguage) {
          newLanguages.push(foundLanguage);
        }
      });
    }

    setSelectedLanguages(newLanguages);
  }, []);

  // Render component
  // **********************************************
  return (
    <div>

      {/* Spoken languages combobox */}
      <Label
        description={t('filters.search.language.label')}
        descriptionHelper={`(${t('global.combobox.box-type')})`}
        descriptionClass="font-bold !mb-2 text-ink-medium"
        className="!mt-0"
        as="div"
      >
        <ComboBox
          placeholder={t('onboarding.step5.example.placeholder')}
          options={comboboxOptions}
          renderList={(item, index, listKey, getItemProps, listStyling) => (
            <li key={listKey} className={`${listStyling} cursor-pointer`} {...getItemProps({ item, index })}>
              {item['label' as keyof typeof item]}
            </li>
          )}
          onSelectedItemChange={(item) => {
            if (item) {
              addLanguage(item as GlobalFilterObjectField);
              submitFunc?.();
            }
          }}
          {...recordFunc('languagesList', {
            id: `${idPrefix || ''}languagesList`,
            removeFromSubmit: true,
          })}
        />
      </Label>

      {/* Selected languages chip list */
        !!selectedLanguages.length && (
          <div className="mt-6 flex flex-wrap gap-2">
            {selectedLanguages.map((language) => (
              <Chip key={language.label} onDelete={() => deleteLanguage(language.recordOptions?.defaultValue as string)}>
                { language.label }
              </Chip>
            ))}
          </div>
        )
      }

      {/* Input that collects the filter languages; use the 'data-filters-label' attribute when dealing with variable filter labels */}
      <Input
        type="hidden"
        value={selectedLanguagesString}
        data-filters-label={selectedLanguagesFiltersLabel}
        onInput={(e) => {
          // If the event is not trusted (e.isTrusted = false), it means it was triggered programmatically
          // (ie: bjForm's setFormValues() function)
          if (!e.isTrusted) buildSelectedLanguagesFromString(e);
        }}
        {...recordFunc('spokenLanguages', {
          id: `${idPrefix || ''}spokenLanguages`,
          convertValue: 'commaStringToArray',
        })}
      />
    </div>
  );
};
